import React, { useEffect } from "react";
import {
    Shield,
    FileText,
    Scale,
    CreditCard,
    UserCheck,
    Globe2,
    AlertTriangle,
    AlertCircle,
    Mail,
    ArrowLeft,
    Clock,
  } from "lucide-react";
  
import { Link } from "react-router-dom";
import { motion, useAnimation } from "framer-motion";

// Animation variants
const sectionReveal = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.16, 1, 0.3, 1],
    },
  },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.1,
    },
  },
};

const TermsOfService = () => {
  const controls = useAnimation();

  useEffect(() => {
    window.scrollTo(0, 0);
    controls.start("visible");
  }, [controls]);

  const termsSections = [
    {
      icon: <FileText className="w-6 h-6 text-yellow-400" />,
      title: "Acceptance of Terms",
      content: (
        <div className="space-y-3">
          <p className="text-slate-600 leading-relaxed">
            By accessing or using{" "}
            <span className="font-semibold text-slate-900">Rakfort</span> (the
            &quot;Service&quot;), you agree to be bound by these Terms &
            Conditions (&quot;Terms&quot;). If you do not agree, you must not
            use the Service.
          </p>
          <p className="text-slate-600 leading-relaxed">
            These Terms apply to all visitors, users, and others who access or
            use the Service.
          </p>
        </div>
      ),
    },
    {
      icon: <UserCheck className="w-6 h-6 text-yellow-400" />,
      title: "Eligibility & Accounts",
      content: (
        <div className="space-y-3">
          <p className="text-slate-600 leading-relaxed">
            You must be at least 18 years old or have legal capacity under your
            jurisdiction&apos;s laws to use the Service.
          </p>
          <p className="text-slate-600 leading-relaxed">
            When you create an account, you agree to provide accurate, complete,
            and current information and to keep it updated. You are responsible
            for safeguarding your login credentials and for all activities under
            your account.
          </p>
        </div>
      ),
    },
    {
      icon: <Shield className="w-6 h-6 text-yellow-400" />,
      title: "Use of the Service",
      content: (
        <div className="space-y-3">
          <p className="text-slate-600 leading-relaxed">
            You agree to use the Service only for lawful purposes and in
            accordance with these Terms and any applicable laws or regulations.
          </p>
          <p className="text-slate-600 leading-relaxed">
            We reserve the right to refuse access to the Service, suspend
            accounts, or remove content at our sole discretion if we believe
            these Terms have been violated.
          </p>
        </div>
      ),
    },
    {
      icon: <CreditCard className="w-6 h-6 text-yellow-400" />,
      title: "Subscriptions & Payments",
      content: (
        <div className="space-y-3">
          <p className="text-slate-600 leading-relaxed">
            Certain features of the Service may be offered on a paid
            subscription basis. You agree to provide accurate billing
            information and authorize us or our payment processors to charge
            your payment method.
          </p>
          <p className="text-slate-600 leading-relaxed">
            Fees, billing cycles, and renewal terms will be presented at the
            time of purchase. Unless otherwise stated, subscriptions renew
            automatically until cancelled.
          </p>
        </div>
      ),
    },
    {
      icon: <Scale className="w-6 h-6 text-yellow-400" />,
      title: "Intellectual Property",
      content: (
        <div className="space-y-3">
          <p className="text-slate-600 leading-relaxed">
            All content, features, and functionality on the Service—including
            but not limited to text, graphics, logos, icons, and software—are
            the property of Rakfort or its licensors and are protected by
            applicable intellectual property laws.
          </p>
          <p className="text-slate-600 leading-relaxed">
            You are granted a limited, non-exclusive, non-transferable license
            to access and use the Service for your personal or internal business
            use, subject to these Terms.
          </p>
        </div>
      ),
    },
    {
      icon: <AlertTriangle className="w-6 h-6 text-yellow-400" />,
      title: "Prohibited Activities",
      content: (
        <div className="space-y-3">
          <p className="text-slate-600 leading-relaxed">
            You agree not to engage in any activity that:
          </p>
          <ul className="list-disc list-inside text-slate-600 space-y-1">
            <li>Violates any applicable law or regulation</li>
            <li>
              Attempts to gain unauthorized access to the Service, accounts, or
              systems
            </li>
            <li>
              Interferes with or disrupts the integrity or performance of the
              Service
            </li>
            <li>
              Copies, modifies, distributes, or reverse engineers any part of
              the Service except as expressly allowed
            </li>
          </ul>
        </div>
      ),
    },
    {
      icon: <Globe2 className="w-6 h-6 text-yellow-400" />,
      title: "Third-Party Services & Links",
      content: (
        <div className="space-y-3">
          <p className="text-slate-600 leading-relaxed">
            The Service may contain links to third-party websites or integrate
            third-party services. We do not control and are not responsible for
            the content, privacy policies, or practices of any third parties.
          </p>
          <p className="text-slate-600 leading-relaxed">
            Your use of third-party services is at your own risk and subject to
            their terms and policies.
          </p>
        </div>
      ),
    },
    {
      icon: <AlertCircle className="w-6 h-6 text-yellow-400" />,
      title: "Disclaimers & Limitation of Liability",
      content: (
        <div className="space-y-3">
          <p className="text-slate-600 leading-relaxed">
            The Service is provided on an &quot;AS IS&quot; and &quot;AS
            AVAILABLE&quot; basis without warranties of any kind, whether
            express or implied.
          </p>
          <p className="text-slate-600 leading-relaxed">
            To the maximum extent permitted by law, Rakfort and its affiliates
            will not be liable for any indirect, incidental, special, or
            consequential damages arising out of or in connection with your use
            of the Service.
          </p>
        </div>
      ),
    },
    {
      icon: <Clock className="w-6 h-6 text-yellow-400" />,
      title: "Termination",
      content: (
        <div className="space-y-3">
          <p className="text-slate-600 leading-relaxed">
            We may suspend or terminate your access to the Service at any time,
            with or without cause or notice, including if you violate these
            Terms.
          </p>
          <p className="text-slate-600 leading-relaxed">
            Upon termination, your right to use the Service will immediately
            cease, but provisions that by their nature should survive will
            remain in effect (including ownership, warranty disclaimers, and
            limitations of liability).
          </p>
        </div>
      ),
    },
    {
      icon: <Scale className="w-6 h-6 text-yellow-400" />,
      title: "Governing Law & Changes to Terms",
      content: (
        <div className="space-y-3">
          <p className="text-slate-600 leading-relaxed">
            These Terms are governed by and construed in accordance with the
            laws of the applicable jurisdiction where Rakfort operates, without
            regard to its conflict of law provisions.
          </p>
          <p className="text-slate-600 leading-relaxed">
            We may update these Terms from time to time. Changes will be
            effective when posted on this page. Your continued use of the
            Service after changes become effective constitutes acceptance of the
            updated Terms.
          </p>
        </div>
      ),
    },
  ];

  return (
    <div className="bg-white text-[#2c2c2c]">
      <div className="max-w-[1140px] mx-auto px-5 pt-8 pb-2">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#005a9c] hover:text-[#004a82]">
          <ArrowLeft className="w-4 h-4" />
          Home
        </Link>
      </div>
      <section className="border-b border-[#e8e8e8] bg-[#f0f7ff] py-10 md:py-12">
        <div className="max-w-[1140px] mx-auto px-5">
          <p className="text-xs font-medium uppercase tracking-[0.12em] text-[#737373] mb-2">Legal</p>
          <h1 className="text-2xl md:text-[1.75rem] font-semibold text-[#2c2c2c] tracking-tight">Terms &amp; conditions</h1>
          <p className="mt-3 text-[#5c5c5c] max-w-2xl text-[0.95rem]">
            Please read these terms carefully. They define your rights and responsibilities when using RakFort.
          </p>
        </div>
      </section>

      {/* Terms Content */}
      <section className="relative py-12 px-5 md:px-8 bg-white">
        <div className="max-w-5xl mx-auto">
          <motion.div
            className="space-y-16"
            initial="hidden"
            animate={controls}
            variants={staggerContainer}
          >
            {termsSections.map((section, index) => (
              <motion.div
                key={index}
                className="border border-[#e8e8e8] rounded-sm p-6 md:p-8 bg-[#f0f7ff]"
                variants={sectionReveal}
              >
                <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                  <div className="p-2 bg-white border border-[#e8e8e8] rounded-sm shrink-0">
                    {React.cloneElement(section.icon, {
                      className: "w-6 h-6 text-[#005a9c]",
                    })}
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-[#2c2c2c]">{section.title}</h2>
                    <div className="mt-3 text-[0.9375rem] text-[#5c5c5c]">{section.content}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>

          <motion.div className="mt-16 text-center border border-[#e8e8e8] rounded-sm p-8 bg-[#f0f7ff]" variants={sectionReveal}>
            <h3 className="text-lg font-semibold text-[#2c2c2c] mb-2">Questions</h3>
            <p className="text-[#5c5c5c] text-sm max-w-xl mx-auto mb-4">
              If you have any questions about these terms, contact us at:
            </p>
            <a href="mailto:info@rakfort.com" className="inline-flex items-center gap-2 text-[#005a9c] font-medium hover:text-[#004a82]">
              <Mail className="w-4 h-4" />
              info@rakfort.com
            </a>
            <p className="text-xs text-[#737373] mt-4">
              This page is for informational purposes only and does not constitute legal advice.
            </p>
          </motion.div>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-5 pb-12 text-center text-sm text-[#737373]">
        <p>Last updated: November 11, 2023</p>
      </div>
    </div>
  );
};

export default TermsOfService;
